import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Trash2, RotateCcw, Loader2, School, GraduationCap, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { TeacherShell } from "@/components/TeacherShell";

function daysBadge(n) {
  const tone = n <= 5 ? "bg-rose-50 text-rose-600 dark:bg-rose-500/10" : "bg-slate-100 text-slate-500 dark:bg-white/10";
  return (
    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${tone}`}>
      {n > 0 ? `${n} j restant${n > 1 ? "s" : ""}` : "Suppression imminente"}
    </span>
  );
}

export default function TrashPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [purgeTarget, setPurgeTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/trash");
      setData(data);
    } catch {
      toast.error("Chargement de la corbeille impossible.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const restore = async (type, id) => {
    setBusy(`${type}-${id}`);
    try {
      await api.post(`/trash/${type}/${id}/restore`);
      toast.success(type === "classes" ? "Classe restaurée." : "Élève restauré.");
      load();
    } catch (e) {
      if (type === "students" && e.response?.status === 404) {
        toast.error("Restaurez d'abord la classe de cet élève.");
      } else {
        toast.error("Restauration impossible.");
      }
    } finally {
      setBusy(null);
    }
  };

  const purge = async () => {
    if (!purgeTarget) return;
    const { type, id } = purgeTarget;
    setBusy(`purge-${id}`);
    try {
      await api.delete(`/trash/${type}/${id}`);
      toast.success("Suppression définitive effectuée.");
      setPurgeTarget(null);
      load();
    } catch {
      toast.error("Suppression impossible.");
    } finally {
      setBusy(null);
    }
  };

  const isEmpty = data && data.classes.length === 0 && data.students.length === 0;

  return (
    <TeacherShell active="corbeille">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <div className="mb-6">
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">Corbeille</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Les éléments supprimés sont conservés {data?.retention_days ?? 30} jours puis effacés définitivement. Vous pouvez les restaurer à tout moment.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-400" size={26} /></div>
        ) : isEmpty ? (
          <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-slate-200 py-16 text-center dark:border-white/10" data-testid="trash-empty">
            <Trash2 className="text-slate-300" size={36} />
            <p className="text-sm text-slate-500">La corbeille est vide.</p>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="flex flex-col gap-6">
            {data.classes.length > 0 && (
              <section data-testid="trash-classes">
                <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Classes</h2>
                <div className="flex flex-col gap-2">
                  {data.classes.map((c) => (
                    <div key={c.id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-white px-4 py-3 dark:border-white/10 dark:bg-white/[0.03]" data-testid={`trash-class-${c.id}`}>
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500 dark:bg-white/10"><School size={17} /></span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">{c.name}{c.level ? ` · ${c.level}` : ""}</p>
                          <p className="text-xs text-slate-400">{c.student_count} élève{c.student_count > 1 ? "s" : ""}</p>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {daysBadge(c.days_left)}
                        <Button size="sm" variant="outline" className="rounded-lg" onClick={() => restore("classes", c.id)} disabled={busy === `classes-${c.id}`} data-testid={`restore-class-${c.id}`}>
                          <RotateCcw size={15} className="mr-1.5" /> Restaurer
                        </Button>
                        <button onClick={() => setPurgeTarget({ type: "classes", id: c.id, name: c.name })} className="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600" aria-label="Supprimer définitivement" data-testid={`purge-class-${c.id}`}><Trash2 size={16} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {data.students.length > 0 && (
              <section data-testid="trash-students">
                <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Élèves</h2>
                <div className="flex flex-col gap-2">
                  {data.students.map((s) => (
                    <div key={s.id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-white px-4 py-3 dark:border-white/10 dark:bg-white/[0.03]" data-testid={`trash-student-${s.id}`}>
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500 dark:bg-white/10"><GraduationCap size={17} /></span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">{s.name}</p>
                          <p className="truncate text-xs text-slate-400">{s.class_name}</p>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {daysBadge(s.days_left)}
                        <Button size="sm" variant="outline" className="rounded-lg" onClick={() => restore("students", s.id)} disabled={busy === `students-${s.id}`} data-testid={`restore-student-${s.id}`}>
                          <RotateCcw size={15} className="mr-1.5" /> Restaurer
                        </Button>
                        <button onClick={() => setPurgeTarget({ type: "students", id: s.id, name: s.name })} className="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600" aria-label="Supprimer définitivement" data-testid={`purge-student-${s.id}`}><Trash2 size={16} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </motion.div>
        )}
      </div>

      <Dialog open={!!purgeTarget} onOpenChange={(v) => !v && setPurgeTarget(null)}>
        <DialogContent className="rounded-2xl sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle size={18} className="text-rose-500" /> Suppression définitive</DialogTitle>
            <DialogDescription>
              « {purgeTarget?.name} » sera supprimé <strong>définitivement</strong>, sans possibilité de restauration. Toutes les données associées seront perdues.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setPurgeTarget(null)}>Annuler</Button>
            <Button className="rounded-xl bg-rose-600 text-white hover:bg-rose-700" onClick={purge} disabled={busy === `purge-${purgeTarget?.id}`} data-testid="confirm-purge">
              {busy === `purge-${purgeTarget?.id}` ? <Loader2 size={16} className="animate-spin" /> : "Supprimer définitivement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TeacherShell>
  );
}
